import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  CreateDrilldownFilterPresetRequest,
  CreateFilterPresetRequest,
  DashboardMode,
  DrilldownFilterPresetDto,
  FilterPresetDto,
  FilterPresetSurface,
} from "@nexus/contracts";
import { authFetch } from "@/lib/api-client";

export const filterPresetKeys = {
  all: ["filter-presets"] as const,
  lists: () => [...filterPresetKeys.all, "list"] as const,
  list: (surface: FilterPresetSurface, mode: DashboardMode) =>
    [...filterPresetKeys.lists(), surface, mode] as const,
};

async function fetchOverviewFilterPresets(mode: DashboardMode): Promise<FilterPresetDto[]> {
  const { data } = await authFetch<FilterPresetDto[]>(
    `/overview/filter-presets?mode=${encodeURIComponent(mode)}`,
  );
  return data ?? [];
}

async function fetchDrilldownFilterPresets(mode: DashboardMode): Promise<DrilldownFilterPresetDto[]> {
  const { data } = await authFetch<DrilldownFilterPresetDto[]>(
    `/drilldown/filter-presets?mode=${encodeURIComponent(mode)}`,
  );
  return data ?? [];
}

export function overviewFilterPresetsQueryOptions(mode: DashboardMode) {
  return queryOptions({
    queryKey: filterPresetKeys.list("overview", mode),
    queryFn: () => fetchOverviewFilterPresets(mode),
  });
}

export function drilldownFilterPresetsQueryOptions(mode: DashboardMode) {
  return queryOptions({
    queryKey: filterPresetKeys.list("drilldown", mode),
    queryFn: () => fetchDrilldownFilterPresets(mode),
  });
}

export function useCreateOverviewFilterPreset(mode: DashboardMode) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateFilterPresetRequest) => {
      const { data } = await authFetch<FilterPresetDto>("/overview/filter-presets", {
        method: "POST",
        body: JSON.stringify(body),
      });
      return data!;
    },
    onSuccess: (created) => {
      queryClient.setQueryData<FilterPresetDto[]>(
        filterPresetKeys.list("overview", mode),
        (old = []) => (old.some((p) => p.id === created.id) ? old : [...old, created]),
      );
      void queryClient.invalidateQueries({ queryKey: filterPresetKeys.list("overview", mode) });
    },
  });
}

export function useCreateDrilldownFilterPreset(mode: DashboardMode) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateDrilldownFilterPresetRequest) => {
      const { data } = await authFetch<DrilldownFilterPresetDto>("/drilldown/filter-presets", {
        method: "POST",
        body: JSON.stringify(body),
      });
      return data!;
    },
    onSuccess: (created) => {
      queryClient.setQueryData<DrilldownFilterPresetDto[]>(
        filterPresetKeys.list("drilldown", mode),
        (old = []) => (old.some((p) => p.id === created.id) ? old : [...old, created]),
      );
      void queryClient.invalidateQueries({ queryKey: filterPresetKeys.list("drilldown", mode) });
    },
  });
}

export function useDeleteFilterPreset(surface: FilterPresetSurface, mode: DashboardMode) {
  const queryClient = useQueryClient();
  const apiPath = `/${surface}/filter-presets`;
  return useMutation({
    mutationFn: async (id: string) => {
      await authFetch(`${apiPath}/${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: filterPresetKeys.list(surface, mode) });
    },
  });
}
